import type { Ace } from '@/accessControl/itemAclModal.utilities';
import type { ACLPermissionName } from '@/main/app.constants';
import { PERMISSIONS } from '@/main/app.constants';
import { Checkbox } from '@/core/Checkbox.atom';
import type { TooltipPosition } from '@seeqdev/qomponents';

interface AceCheckboxProps {
  ace: Ace;
  setPermissions: (permission: ACLPermissionName, ace: Ace) => void;
  name: ACLPermissionName;
  displayOnly?: boolean;
  isAclFromDatasource: boolean;
}

export const AceCheckbox: React.FunctionComponent<AceCheckboxProps> = ({
  ace,
  setPermissions,
  name,
  displayOnly,
  isAclFromDatasource,
}) => {
  const hasLocalPermission = !!ace.permissions && !!ace.permissions[name];
  const hasInheritedPermission = !!ace.inheritedPermissions && !!ace.inheritedPermissions[name];
  const isOwner = !!ace.isOwner;
  const inherited: boolean = isOwner && hasInheritedPermission;
  const placementForPermission: TooltipPosition = name === PERMISSIONS.READ ? 'right' : 'left';

  const canTogglePermissions = !inherited && !displayOnly && !isAclFromDatasource;
  const togglePermissions = () => canTogglePermissions && setPermissions(name, ace);

  /**
   * Returns a translation key that represents the appropriate tooltip text.
   * Local permissions always take precedence.
   *
   * @return the translation key or undefined if no tooltip should show
   */
  const getTooltip: () => string | undefined = () => {
    if (hasLocalPermission || displayOnly) {
      // takes precedence
    } else if (isAclFromDatasource) {
      return 'ACCESS_CONTROL.FROM_DATASOURCE';
    } else if (inherited) {
      return 'ACCESS_CONTROL.INHERITED';
    } else if (isOwner) {
      return 'ACCESS_CONTROL.OWNER_ACCESS';
    }
  };

  const isCheckboxDisabled: () => boolean = () => {
    if (hasLocalPermission || hasInheritedPermission || isOwner) {
      return displayOnly || isAclFromDatasource ? true : !hasLocalPermission;
    } else {
      return displayOnly || !!isAclFromDatasource;
    }
  };

  return (
    <Checkbox
      id={`ace-checkbox-${name}`}
      onClick={togglePermissions}
      isChecked={hasLocalPermission || hasInheritedPermission || isOwner}
      disabled={isCheckboxDisabled()}
      flexAlign="center"
      classes="flexCenter"
      tooltip={getTooltip()}
      tooltipPlacement={placementForPermission}
      isTooltipSimple={true}
    />
  );
};
